import { SupabaseClient } from "@supabase/supabase-js";
import { logAndThrow } from "../utils/logging";
import {
    sanctionCamelToDb,
    sanctionDbToCamel,
} from "../lib/mappers/disciplinary";
import {
    DisciplinaryMatchEntryStatus,
    DisciplinarySubjectType,
    DisciplinarySuspensionType,
    Sanction,
    DisciplinarySettings,
} from "../types";
import type { LicenceStateTab } from "@/lib/players/licenceStateTab";

export class MembershipRepairedError extends Error {
    readonly playerId: string;
    constructor(playerId: string) {
        super("__membership_repaired__");
        this.name = "MembershipRepairedError";
        this.playerId = playerId;
    }
}

export async function savePlayer(
    supabase: SupabaseClient,
    payload: any,
    userId: string | null,
    leagueId?: string,
    season?: string
) {
    if (!payload) return;
    const {
        id,
        firstName,
        lastName,
        firstNameAr,
        lastNameAr,
        dateOfBirth,
        licenseNumber,
        currentTeamId,
        isArchived,
        // Strip UI-only / derived fields
        fullName,
        createdByName,
        updatedByName,
        createdAt,
        updatedAt,
        ...rest // capture any future extras but DO NOT spread into DB row unfiltered
    } = payload;

    const dbRow: any = {
        id,
        first_name: (firstName ?? "").trim().toUpperCase(),
        last_name:  (lastName  ?? "").trim().toUpperCase(),
        first_name_ar: firstNameAr || null,
        last_name_ar: lastNameAr || null,
        date_of_birth: dateOfBirth || null,
        license_number: licenseNumber || null,
        current_team_id: currentTeamId || null,
        is_archived: isArchived ?? false,
        updated_by: userId,
        ...(id ? {} : { created_by: userId }),
    };

    // Basic validation (avoid silent empty names)
    if (!dbRow.first_name || !dbRow.last_name) {
        throw new Error("Le prénom et le nom sont obligatoires pour enregistrer un joueur.");
    }

    // Pre-check for existing license+team to avoid a cryptic DB constraint error.
    // This can happen when a player exists in the DB but is missing a league membership (so they're
    // invisible in the RPC-based roster and the client-side modal validation doesn't catch them).
    if (licenseNumber && currentTeamId) {
        let query = supabase
            .from("players")
            .select("id")
            .eq("license_number", licenseNumber.trim())
            .eq("current_team_id", currentTeamId);
        // When updating an existing player, exclude themselves from the conflict check
        if (id) query = query.neq("id", id);
        let { data: existing } = await query.maybeSingle();

        // Fast path (base column) missed — the licence may belong to another player
        // historically (subject_licenses), e.g. re-licensed across seasons. Resolve it
        // and, if the hit is a different player on this same team, treat it as a duplicate too.
        if (!existing) {
            const { data: resolved } = await supabase.rpc("resolve_subjects_by_licenses", {
                p_licenses: [licenseNumber.trim()],
            });
            const hit = (resolved ?? []).find(
                (r: any) => r.subject_type === "player" && r.subject_id !== id
            );
            if (hit) {
                const { data: hitPlayer } = await supabase
                    .from("players")
                    .select("id")
                    .eq("id", hit.subject_id)
                    .eq("current_team_id", currentTeamId)
                    .maybeSingle();
                if (hitPlayer) existing = hitPlayer;
            }
        }

        if (existing) {
            if (!id && leagueId && season) {
                // New player creation: the existing record is the real player — repair their membership
                await addPlayerLeagueMembership(supabase, existing.id, leagueId, currentTeamId, season, userId);
                throw new MembershipRepairedError(existing.id);
            }
            throw new Error(
                "Ce numéro de licence est déjà associé à un autre joueur de cet effectif. " +
                "Vérifiez les données avant de continuer."
            );
        }
    }

    // Jamais d'upsert : INSERT … ON CONFLICT DO UPDATE SET date_of_birth =
    // EXCLUDED.date_of_birth exige SELECT sur date_of_birth, retiré à authenticated
    // par 20261012235000 (42501). Fiche existante → UPDATE (RETURNING id, colonne
    // lisible : 0 ligne = mise à jour refusée par la RLS) ; nouvelle → INSERT.
    if (id) {
        const { id: _id, ...changes } = dbRow;
        const { data, error } = await supabase
            .from("players")
            .update(changes)
            .eq("id", id)
            .select("id");
        if (error) return logAndThrow("savePlayer", error, { id });
        if (!data || data.length === 0) {
            return logAndThrow("savePlayer", { message: "Joueur introuvable ou modification non autorisée." }, { id });
        }
        return;
    }
    const { error } = await supabase.from("players").insert(dbRow);
    if (error) return logAndThrow("savePlayer", error, { id });
}

export async function archivePlayer(
    supabase: SupabaseClient,
    playerId: string,
    userId: string | null
) {
    const { error } = await supabase
        .from("players")
        .update({ is_archived: true, updated_by: userId })
        .eq("id", playerId);
    if (error) return logAndThrow("archivePlayer", error, { playerId });
}

export async function addPlayerLeagueMembership(
    supabase: SupabaseClient,
    playerId: string,
    leagueId: string,
    teamId: string,
    season: string,
    userId: string | null,
    licenseNumber?: string | null
): Promise<void> {
    // userId is kept for call-site compatibility; the RPC derives the actor from auth.uid().
    void userId;
    const { error } = await supabase.rpc("register_player_membership", {
        p_player_id: playerId,
        p_league_id: leagueId,
        p_season: season,
        p_team_id: teamId,
        p_license: licenseNumber ?? null,
        p_player_number: null,
    });
    if (error) return logAndThrow("addPlayerLeagueMembership", error, { playerId });
}

/**
 * Format d'une licence algérienne : deux chiffres d'année, une lettre de région,
 * deux chiffres, une lettre de type (J joueur, E/D staff), puis la séquence —
 * p. ex. `26N03J0025`. Sert à distinguer une recherche par licence d'une
 * recherche par nom ; on reste tolérant sur la longueur de la séquence.
 */
function looksLikeLicense(term: string): boolean {
    return /^\d{2}[A-Z]\d{2}[A-Z]\d+$/i.test(term.trim());
}

/**
 * Licence → sujet existant, historique compris.
 *
 * `resolve_subjects_by_licenses` interroge `subject_licenses` puis retombe sur la
 * colonne de base : une licence `26…` déposée cette saison retrouve donc la
 * personne déjà en base sous sa `25…`, ce qu'une simple égalité sur
 * `license_number` rate — et c'est précisément ainsi qu'on crée des doublons
 * d'identité en septembre. Les événements FAF (`faf_match_events`) ne portent ni
 * `faf_person_id` ni `fifa_id` : la licence est la seule clé dont ces flux
 * disposent.
 *
 * On ne filtre pas les fiches archivées ici : côté staff l'unicité de la licence
 * les couvre aussi (`team_staff_members_license_number_key`), donc les ignorer ne
 * ferait qu'échanger un doublon contre une violation de contrainte. Plusieurs
 * fiches peuvent porter la même licence dans l'historique (un doublon archivé,
 * par exemple) : la fiche active gagne.
 */
async function resolveSubjectByLicense(
    supabase: SupabaseClient,
    subjectType: "player" | "staff",
    license: string
): Promise<string | null> {
    const table = subjectType === "player" ? "players" : "team_staff_members";
    const trimmed = license.trim();
    if (!trimmed) return null;

    const { data: resolved, error } = await supabase.rpc("resolve_subjects_by_licenses", {
        p_licenses: [trimmed],
    });

    if (error) {
        // Le résolveur est un plus : s'il échoue on retombe sur la colonne de
        // base, c'est-à-dire exactement le comportement d'avant.
        console.error("[disciplinaryService] resolve_subjects_by_licenses:", error.message);
        const { data: fallback } = await supabase
            .from(table)
            .select("id")
            .ilike("license_number", trimmed)
            .eq("is_archived", false)
            .maybeSingle();
        return fallback?.id ?? null;
    }

    const ids = (resolved ?? [])
        .filter((r: any) => r.subject_type === subjectType)
        .map((r: any) => r.subject_id as string);
    if (ids.length === 0) return null;

    const { data: rows } = await supabase
        .from(table)
        .select("id, is_archived")
        .in("id", ids);

    const candidates = rows ?? [];
    return candidates.find((r: any) => !r.is_archived)?.id ?? candidates[0]?.id ?? null;
}

/**
 * Crée (ou rattache) un joueur issu d'un événement FAF et garantit son adhésion
 * de ligue pour la saison. La résolution de licence reste côté client ; les
 * écritures passent par ensure_disciplinary_entry_player (SECURITY DEFINER) :
 * le Commissaire Disciplinaire n'a aucune politique d'écriture sur players ni
 * sur player_league_memberships, l'INSERT direct échouait en RLS.
 */
export async function createPlayerForFafGoal(
    supabase: SupabaseClient,
    params: {
        fullName: string;
        licenseNumber: string | null;
        playerNumber: number | null;
        teamId: string;
        leagueId: string;
        season: string;
        userId: string | null;
    }
): Promise<string> {
    const { fullName, licenseNumber, playerNumber, teamId, leagueId, season } = params;

    const ensurePlayer = async (args: {
        playerId?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        license?: string | null;
        playerNumber?: string | null;
    }): Promise<string> => {
        const { data, error } = await supabase.rpc("ensure_disciplinary_entry_player", {
            p_league_id: leagueId,
            p_season: season,
            p_team_id: teamId,
            p_player_id: args.playerId ?? null,
            p_first_name: args.firstName ?? null,
            p_last_name: args.lastName ?? null,
            p_license: args.license ?? null,
            p_player_number: args.playerNumber ?? null,
        });
        if (error) throw new Error(`Erreur création joueur : ${error.message}`);
        return data as string;
    };

    // 1. La licence désigne-t-elle une personne déjà en base ? Résolution
    //    historique comprise — voir resolveSubjectByLicense.
    if (licenseNumber) {
        const existingId = await resolveSubjectByLicense(supabase, 'player', licenseNumber);
        if (existingId) {
            return ensurePlayer({ playerId: existingId, license: licenseNumber.trim() });
        }

        // 2. Check if this is an old licence from a completed transfer.
        //    Departed memberships now store the old license in the license_number column
        //    (backfilled by migration 20260304000005, set going forward by the fixed RPC).
        //    Use .limit(1) instead of .maybeSingle() — multiple departed memberships can
        //    share the same license (e.g. duplicate was created then departed too).
        const { data: oldMemberships } = await supabase
            .from('player_league_memberships')
            .select('player_id')
            .eq('membership_status', 'departed')
            .ilike('license_number', licenseNumber.trim())
            .limit(1);
        if (oldMemberships && oldMemberships.length > 0) {
            // Player transferred — their new license is now on the players table
            return ensurePlayer({ playerId: oldMemberships[0].player_id });
        }
    }

    // 3. Nouvelle fiche, licence en majuscules
    const parts = fullName.trim().split(/\s+/);
    const firstName = parts[0].toUpperCase();
    const lastName = (parts.slice(1).join(' ') || firstName).toUpperCase();

    return ensurePlayer({
        firstName,
        lastName,
        license: licenseNumber ? licenseNumber.trim().toUpperCase() : null,
        playerNumber: playerNumber != null ? String(playerNumber) : null,
    });
}

/**
 * Creates a staff member from a FAF event name + license (D/E prefix) and returns the new staff's UUID.
 * If a staff member with the same license already exists, returns their existing id.
 * La création passe par create_disciplinary_entry_staff (SECURITY DEFINER) :
 * team_staff_members n'a aucune politique d'écriture pour le Commissaire.
 */
export async function createStaffForFafEvent(
    supabase: SupabaseClient,
    params: {
        fullName: string;
        licenseNumber: string | null;
        teamId: string;
        leagueId: string;
        userId: string | null;
    }
): Promise<string> {
    const { fullName, licenseNumber, teamId, leagueId } = params;

    if (licenseNumber) {
        const existingId = await resolveSubjectByLicense(supabase, 'staff', licenseNumber);
        if (existingId) return existingId;
    }

    const parts = fullName.trim().split(/\s+/);
    const firstName = parts[0].toUpperCase();
    const lastName = (parts.slice(1).join(' ') || firstName).toUpperCase();

    const { data, error } = await supabase.rpc("create_disciplinary_entry_staff", {
        p_league_id: leagueId,
        p_team_id: teamId,
        p_first_name: firstName,
        p_last_name: lastName,
        p_license: licenseNumber ? licenseNumber.trim().toUpperCase() : null,
    });

    if (error) throw new Error(`Erreur création staff : ${error.message}`);

    return data as string;
}

export async function saveSanction(
    supabase: SupabaseClient,
    payload: any,
    userId: string | null
) {
    if (!payload) return;

    // If updating, fetch the existing sanction first for change tracking
    let existingSanction: any = null;
    if (payload.id) {
        const { data } = await supabase
            .from("sanctions")
            .select("*")
            .eq("id", payload.id)
            .single();
        existingSanction = data;
    }

    const dbBase = sanctionCamelToDb(payload);
    const dbRow = {
        ...dbBase,
        updated_by: userId,
        ...(payload.id ? {} : { created_by: userId }),
        id: payload.id,
    };

    if (!dbRow.subject_type) {
        throw new Error("subjectType manquant pour la sanction.");
    }

    switch (dbRow.subject_type as DisciplinarySubjectType) {
        case DisciplinarySubjectType.PLAYER:
            if (!dbRow.player_id) {
                throw new Error("playerId manquant pour la sanction du joueur.");
            }
            break;
        case DisciplinarySubjectType.COACH:
        case DisciplinarySubjectType.STAFF:
            if (!dbRow.staff_member_id) {
                throw new Error("staffMemberId manquant pour la sanction du staff.");
            }
            if (!dbRow.team_id) {
                throw new Error("teamId manquant pour la sanction du staff.");
            }
            break;
        case DisciplinarySubjectType.TEAM:
            if (!dbRow.team_id) {
                throw new Error("teamId manquant pour la sanction d'équipe.");
            }
            break;
        default:
            break;
    }

    if (!dbRow.type) throw new Error("type de sanction manquant.");
    if (!dbRow.decision_date) throw new Error("decisionDate manquante.");

    if (dbRow.suspension_type === DisciplinarySuspensionType.DATE_RANGE) {
        if (!dbRow.suspension_start_date || !dbRow.suspension_end_date) {
            throw new Error(
                "Les dates de début et de fin sont obligatoires pour une suspension par période."
            );
        }
    }

    // Track suspension changes
    if (existingSanction && userId) {
        await trackSuspensionChanges(
            existingSanction,
            dbRow,
            userId
        );
    }

    const { error } = await supabase.from("sanctions").upsert(dbRow);
    if (error) return logAndThrow("saveSanction", error, { id: payload.id });
}

/**
 * Track changes to suspension-related fields
 */
async function trackSuspensionChanges(
    oldSanction: any,
    newSanction: any,
    userId: string
) {
    const suspensionFields = [
        'suspension_matches',
        'suspension_match_count',
        'suspension_start_date',
        'suspension_end_date',
        'suspension_type',
        'is_cancelled',
        'fine_amount',
        'reason',
    ];

    const changes: any[] = [];

    for (const field of suspensionFields) {
        const oldValue = oldSanction[field];
        const newValue = newSanction[field];

        // Check if value changed
        if (oldValue !== newValue) {
            changes.push({
                sanction_id: oldSanction.id,
                field_changed: field,
                old_value: oldValue,
                new_value: newValue,
                changed_by: userId,
                changed_at: new Date().toISOString(),
            });
        }
    }

    // Audit logging is handled automatically by unified_audit_trigger
    // The UPDATE operation on sanctions table triggers the audit log creation
    // No manual insert needed to avoid duplicate entries
}

export async function cancelSanction(
    supabase: SupabaseClient,
    sanctionId: string,
    userId: string | null
) {
    const { error } = await supabase
        .from("sanctions")
        .update({ is_cancelled: true, faf_event_id: null, updated_by: userId })
        .eq("id", sanctionId);
    if (error) return logAndThrow("cancelSanction", error, { sanctionId });
}

export async function linkSanctionToFafEvent(
    supabase: SupabaseClient,
    sanctionId: string,
    fafEventId: string,
    userId: string | null
) {
    const { error } = await supabase
        .from("sanctions")
        .update({ faf_event_id: fafEventId, updated_by: userId })
        .eq("id", sanctionId);
    if (error) {
        if (error.code === '23505') return; // already linked — treat as success
        return logAndThrow("linkSanctionToFafEvent", error, { sanctionId, fafEventId });
    }
}

export async function batchLinkSanctionsToFafEvents(
    supabase: SupabaseClient,
    pairs: { sanctionId: string; fafEventId: string }[],
    userId: string | null
) {
    await Promise.all(
        pairs.map(async ({ sanctionId, fafEventId }) => {
            const { error } = await supabase
                .from("sanctions")
                .update({ faf_event_id: fafEventId, updated_by: userId })
                .eq("id", sanctionId);
            if (error) {
                // 23505 = unique_violation: faf_event_id already linked — treat as success
                if (error.code === '23505') return;
                logAndThrow("batchLinkSanctionsToFafEvents", error, { sanctionId, fafEventId });
            }
        })
    );
}

/**
 * Passe par set_match_disciplinary_entry_status : le Commissaire n'a pas de
 * politique UPDATE sur matches, l'UPDATE direct touchait 0 ligne sans erreur.
 * Le RPC refuse explicitement (42501) au lieu d'un faux succès.
 */
export async function updateMatchDisciplinaryEntryStatus(
    supabase: SupabaseClient,
    matchId: string,
    status: DisciplinaryMatchEntryStatus
) {
    const { error } = await supabase.rpc("set_match_disciplinary_entry_status", {
        p_match_id: matchId,
        p_status: status,
    });

    if (error)
        return logAndThrow("updateMatchDisciplinaryEntryStatus", error, {
            matchId,
            status,
        });
}

export async function fetchSuspensionStatuses(
    supabase: SupabaseClient,
    leagueId?: string,
    season?: string,
    playerIds?: string[]
): Promise<Record<string, { isSuspended: boolean; remainingMatches: number }>> {
    const { data, error } = await supabase.functions.invoke("get-suspensions", {
        body: { leagueId, season, playerIds },
    });

    if (error) return logAndThrow("fetchSuspensionStatuses", error, { leagueId, season });

    return data;
}

export interface LeaguePlayersRequest {
    leagueId?: string; // Optional when allLeagues is true
    season: string;
    search?: string;
    teamId?: string;
    page?: number;
    pageSize?: number;
    includeArchived?: boolean;
    sortBy?: "name" | "team" | "suspension" | "birthDate" | "license";
    sortDir?: "asc" | "desc";
    allLeagues?: boolean; // When true, fetch players from all leagues
    showPlayersWithoutTeam?: boolean;
    /** Résolution par id (lien « Fiche joueur ») : ignore `search`, garde la portée. */
    playerIds?: string[];
    /** Sous-onglet d'état de licence. Omis = toutes les lignes (comportement historique). */
    licenceState?: LicenceStateTab;
    /** Groupe de la ligue : `plm.team_id` doit appartenir à `league_group_teams`. */
    leagueGroupId?: string;
    /**
     * Catégorie d'âge (`?cat=`) : l'équipe de l'adhésion active doit avoir
     * `teams.category_id` = cette valeur. Mono-ligue uniquement ; omis = toutes.
     */
    categoryId?: string | null;
}

export interface LeaguePlayersResponse {
    data: any[];
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
    suspendedCount: number;
}

export async function fetchLeaguePlayers(
    supabase: SupabaseClient,
    request: LeaguePlayersRequest
): Promise<LeaguePlayersResponse> {
    // Single-league mode: call the RPC directly (bypasses edge function cold start)
    if (!request.allLeagues && request.leagueId) {
        const { data: rpcResult, error: rpcError } = await supabase.rpc("get_league_players_paginated", {
            p_league_id: request.leagueId,
            p_season: request.season,
            p_page: request.page || 1,
            p_page_size: request.pageSize || 25,
            p_search_term: request.search || null,
            p_team_id: request.teamId || null,
            p_show_without_team: request.showPlayersWithoutTeam || false,
            p_include_archived: request.includeArchived || false,
            p_sort_by: request.sortBy || "suspension",
            p_sort_dir: request.sortDir || "asc",
            // Omis plutôt qu'envoyé à null : l'appel de la liste garde la signature
            // d'avant `20260923170000_paginated_lookup_by_id.sql`.
            ...(request.playerIds?.length ? { p_player_ids: request.playerIds } : {}),
            // Omis plutôt que null : une RPC antérieure à 20260924120000 reste appelable.
            ...(request.licenceState ? { p_licence_state: request.licenceState } : {}),
            ...(request.leagueGroupId ? { p_league_group_id: request.leagueGroupId } : {}),
            // Omis plutôt que null : une RPC antérieure à 20261009140000 reste appelable.
            ...(request.categoryId ? { p_category_id: request.categoryId } : {}),
        });

        if (rpcError) {
            throw new Error(`fetchLeaguePlayers: ${rpcError.message}`);
        }

        const { data: rows, total_count: totalCount, missing_cache_player_ids: missingIds } = rpcResult;

        const responseData = (rows || []).map((row: any) => ({
            id: row.id,
            firstName: row.first_name,
            lastName: row.last_name,
            fullName: `${row.first_name} ${row.last_name}`,
            firstNameAr: row.first_name_ar,
            lastNameAr: row.last_name_ar,
            fullNameAr: `${row.first_name_ar || ''} ${row.last_name_ar || ''}`.trim(),
            dateOfBirth: row.date_of_birth,
            licenseNumber: row.license_number,
            // Licence de LA saison lue, et son club déposant : `license_number`
            // ci-dessus est un scalaire sans saison que l'import n'écrit que
            // lorsque le club du joueur dépose, donc il reste sur l'ancien numéro
            // pour qui a signé ailleurs. Cf. `lib/players/seasonLicence.ts`.
            seasonLicenseNumber: row.season_license_number ?? null,
            seasonLicenseTeamId: row.season_license_team_id ?? null,
            seasonLicenseTeamName: row.season_license_team_name ?? null,
            seasonLicenseTeamNameAr: row.season_license_team_name_ar ?? null,
            priorLicenseNumber: row.prior_license_number ?? null,
            priorLicenseSeason: row.prior_license_season ?? null,
            licenceState: row.licence_state ?? null,
            playerNumber: row.player_number,
            photoUrl: row.photo_url ?? null,
            processedPhotoUrl: row.processed_photo_url ?? null,
            currentTeamId: row.current_team_id,
            currentTeamName: row.current_team_name,
            leagueId: row.league_id,
            leagueName: row.league_name,
            membershipTeamId: row.membership_team_id,
            membershipTeamName: row.membership_team_name ?? null,
            isArchived: row.is_archived,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
            isSuspended: row.is_suspended,
            remainingMatches: row.remaining_matches,
            sanctionCount: row.sanction_count,
            suspensionType: row.suspension_type || null,
        }));

        const page = request.page || 1;
        const pageSize = request.pageSize || 25;

        // Fire-and-forget: trigger cache sync for missing players
        const missingPlayerIds = missingIds || [];
        if (missingPlayerIds.length > 0) {
            supabase.functions.invoke("sync-suspension-cache", {
                body: { leagueId: request.leagueId, season: request.season, playerIds: missingPlayerIds },
            }).catch(() => {});
        }

        return {
            data: responseData,
            count: totalCount,
            page,
            pageSize,
            totalPages: Math.ceil(totalCount / pageSize),
            suspendedCount: responseData.filter((p: any) => p.isSuspended).length,
        };
    }

    // All-leagues mode: use the all-leagues RPC (bypasses edge function)
    if (request.allLeagues) {
        const { data: rpcResult, error: rpcError } = await supabase.rpc("get_all_leagues_players_paginated", {
            p_season: request.season,
            p_page: request.page || 1,
            p_page_size: request.pageSize || 25,
            p_search_term: request.search || null,
            p_team_id: request.teamId || null,
            p_include_archived: request.includeArchived || false,
            p_sort_by: request.sortBy || "name",
            p_sort_dir: request.sortDir || "asc",
            // Omis plutôt que null : une RPC antérieure à 20260924140000 reste appelable.
            ...(request.licenceState ? { p_licence_state: request.licenceState } : {}),
        });

        if (rpcError) {
            throw new Error(`fetchLeaguePlayers (all-leagues RPC): ${rpcError.message}`);
        }

        const { data: rows, total_count: totalCount, missing_cache_entries: missingEntries } = rpcResult;

        const responseData = (rows || []).map((row: any) => ({
            id: row.id,
            firstName: row.first_name,
            lastName: row.last_name,
            fullName: `${row.first_name} ${row.last_name}`,
            firstNameAr: row.first_name_ar,
            lastNameAr: row.last_name_ar,
            fullNameAr: `${row.first_name_ar || ''} ${row.last_name_ar || ''}`.trim(),
            dateOfBirth: row.date_of_birth,
            licenseNumber: row.license_number,
            // Mêmes colonnes de saison que la variante mono-ligue : la vue
            // « toutes ligues » affiche la même cellule de licence.
            seasonLicenseNumber: row.season_license_number ?? null,
            seasonLicenseTeamId: row.season_license_team_id ?? null,
            seasonLicenseTeamName: row.season_license_team_name ?? null,
            seasonLicenseTeamNameAr: row.season_license_team_name_ar ?? null,
            priorLicenseNumber: row.prior_license_number ?? null,
            priorLicenseSeason: row.prior_license_season ?? null,
            licenceState: row.licence_state ?? null,
            playerNumber: row.player_number,
            photoUrl: row.photo_url ?? null,
            processedPhotoUrl: row.processed_photo_url ?? null,
            currentTeamId: row.current_team_id,
            currentTeamName: row.current_team_name,
            leagueId: row.league_id,
            leagueName: row.league_name,
            membershipTeamId: row.membership_team_id,
            membershipTeamName: row.membership_team_name ?? null,
            isArchived: row.is_archived,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
            isSuspended: row.is_suspended,
            remainingMatches: row.remaining_matches,
            sanctionCount: row.sanction_count,
            suspensionType: row.suspension_type || null,
        }));

        const page = request.page || 1;
        const pageSize = request.pageSize || 25;

        // Fire-and-forget: trigger cache sync for missing players, grouped by league
        const missingCacheEntries = missingEntries || [];
        if (missingCacheEntries.length > 0) {
            const byLeague: Record<string, string[]> = {};
            for (const entry of missingCacheEntries) {
                if (!byLeague[entry.league_id]) byLeague[entry.league_id] = [];
                byLeague[entry.league_id].push(entry.player_id);
            }
            for (const [leagueId, playerIds] of Object.entries(byLeague)) {
                supabase.functions.invoke("sync-suspension-cache", {
                    body: { leagueId, season: request.season, playerIds },
                }).catch(() => {});
            }
        }

        return {
            data: responseData,
            count: totalCount,
            page,
            pageSize,
            totalPages: Math.ceil(totalCount / pageSize),
            suspendedCount: responseData.filter((p: any) => p.isSuspended).length,
        };
    }

    // Fallback: edge function (should not be reached in normal flow)
    const { data, error } = await supabase.functions.invoke("get-league-players", {
        body: request,
    });

    if (error) {
        throw new Error(`fetchLeaguePlayers: ${error.message}`);
    }

    return data;
}

export interface LeagueStaffRequest {
    leagueId?: string;
    leagueIds?: string[];
    season: string;
    search?: string;
    teamId?: string;
    groupType?: string;
    statusFilter?: string;
    leagueGroupId?: string;
    includeArchived?: boolean;
    sortBy?: "name" | "team" | "suspension" | "role" | "license" | "sanctions" | "lastSanction";
    sortDir?: "asc" | "desc";
    page?: number;
    pageSize?: number;
    allLeagues?: boolean;
    /** Résolution par id (lien « Fiche encadrant ») : ignore `search`, garde la portée. Mono-ligue uniquement. */
    staffIds?: string[];
    /** Sous-onglet d'état de licence. Omis = toutes les lignes (comportement historique). */
    licenceState?: LicenceStateTab;
    /**
     * Catégorie d'âge (`?cat=`) : l'équipe affichée de l'encadrant doit avoir
     * `teams.category_id` = cette valeur. Mono-ligue uniquement ; omis = toutes.
     */
    categoryId?: string | null;
}

export interface LeagueStaffResponse {
    data: any[];
    count: number;
    suspendedCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export async function fetchLeagueStaff(
    supabase: SupabaseClient,
    request: LeagueStaffRequest
): Promise<LeagueStaffResponse> {
    // Single-league mode: call the RPC directly (bypasses edge function cold start)
    if (!request.allLeagues && request.leagueId) {
        const { data: rpcResult, error: rpcError } = await supabase.rpc("get_league_staff_paginated", {
            p_league_id: request.leagueId,
            p_season: request.season,
            p_page: request.page || 1,
            p_page_size: request.pageSize || 25,
            p_search_term: request.search || null,
            p_team_id: request.teamId || null,
            p_group_type: request.groupType || null,
            p_status_filter: request.statusFilter || "active",
            p_league_group_id: request.leagueGroupId || null,
            p_sort_by: request.sortBy || "suspension",
            p_sort_dir: request.sortDir || "asc",
            ...(request.staffIds?.length ? { p_staff_ids: request.staffIds } : {}),
            ...(request.licenceState ? { p_licence_state: request.licenceState } : {}),
            // Omis plutôt que null : une RPC antérieure à 20261009140000 reste appelable.
            ...(request.categoryId ? { p_category_id: request.categoryId } : {}),
        });

        if (rpcError) {
            throw new Error(`fetchLeagueStaff: ${rpcError.message}`);
        }

        const { data: rows, total_count: totalCount, missing_cache_staff_ids: missingIds } = rpcResult;

        const responseData = (rows || []).map((row: any) => ({
            id: row.id,
            firstName: row.first_name,
            lastName: row.last_name,
            fullName: row.full_name || `${row.first_name} ${row.last_name}`,
            firstNameAr: row.first_name_ar,
            lastNameAr: row.last_name_ar,
            fullNameAr: row.full_name_ar || `${row.first_name_ar || ''} ${row.last_name_ar || ''}`.trim(),
            dateOfBirth: row.date_of_birth,
            // Le scalaire sans saison, conservé pour les modales et le
            // rapprochement FAF. La liste, elle, affiche la licence DE LA SAISON
            // via les quatre champs qui suivent — voir la migration
            // `20260922000005_staff_season_license.sql`.
            licenseNumber: row.license_number,
            seasonLicenseNumber: row.season_license_number ?? null,
            seasonLicenseTeamId: row.season_license_team_id ?? null,
            seasonLicenseTeamName: row.season_license_team_name ?? null,
            seasonLicenseTeamNameAr: row.season_license_team_name_ar ?? null,
            priorLicenseNumber: row.prior_license_number ?? null,
            priorLicenseSeason: row.prior_license_season ?? null,
            licenceState: row.licence_state ?? null,
            photoUrl: row.photo_url ?? null,
            processedPhotoUrl: row.processed_photo_url ?? null,
            email: row.email,
            phone: row.phone,
            teamId: row.team_id,
            teamName: row.team_name,
            // `tslm.team_id` est le club de l'adhésion, donc l'autoritaire ; le
            // scalaire `tsm.team_id` reste au-dessus pour les appelants qui le
            // lisent déjà.
            membershipTeamId: row.membership_team_id ?? null,
            membershipTeamName: row.membership_team_name ?? null,
            groupType: row.group_type,
            role: row.role,
            isActive: row.is_active,
            isArchived: row.is_archived,
            notes: row.notes,
            createdAt: row.created_at,
            updatedAt: row.updated_at,
            isSuspended: row.is_suspended,
            remainingMatches: row.remaining_matches,
            suspensionLabel: row.suspension_reason || null,
            suspensionType: row.suspension_type || null,
            suspensionStartDate: row.suspension_start_date || null,
            suspensionEndDate: row.suspension_end_date || null,
            sanctionCount: row.sanction_count,
            lastSanctionDate: row.last_sanction_date || null,
            latestSanctionDate: row.last_sanction_date || null,
        }));

        const page = request.page || 1;
        const pageSize = request.pageSize || 25;

        // Fire-and-forget: trigger cache sync for missing staff
        const missingStaffIds = missingIds || [];
        if (missingStaffIds.length > 0) {
            supabase.functions.invoke("sync-staff-suspension-cache", {
                body: { leagueId: request.leagueId, season: request.season, staffIds: missingStaffIds },
            }).catch(() => {});
        }

        return {
            data: responseData,
            count: totalCount,
            page,
            pageSize,
            totalPages: Math.ceil(totalCount / pageSize),
            suspendedCount: responseData.filter((s: any) => s.isSuspended).length,
        };
    }

    // Recherche par ancienne licence, mode « toutes les ligues » : l'edge function
    // get-league-staff filtre en mémoire sur la SEULE licence courante (index.ts
    // ~L273-277), contrairement à get_league_staff_paginated qui sait lire
    // subject_licenses. Plutôt que de changer la signature d'une fonction partagée,
    // on traduit le terme : si l'utilisateur tape une licence d'une saison passée,
    // on la résout vers la personne et on interroge avec SA licence courante. Le
    // filtre en mémoire matche alors, et la recherche par nom n'est pas touchée.
    let body: LeagueStaffRequest = request;
    const searchTerm = request.search?.trim() ?? "";
    if (searchTerm && looksLikeLicense(searchTerm)) {
        const staffId = await resolveSubjectByLicense(supabase, "staff", searchTerm);
        if (staffId) {
            const { data: staffRow } = await supabase
                .from("team_staff_members")
                .select("license_number")
                .eq("id", staffId)
                .maybeSingle();
            const currentLicense = (staffRow?.license_number ?? "").trim();
            if (currentLicense && currentLicense.toLowerCase() !== searchTerm.toLowerCase()) {
                body = { ...request, search: currentLicense };
            }
        }
    }

    // allLeagues mode: still use the edge function (heavier computation needed server-side)
    const { data, error } = await supabase.functions.invoke("get-league-staff", {
        body,
    });

    if (error) {
        throw new Error(`fetchLeagueStaff: ${error.message}`);
    }

    return data;
}

export interface DisciplinaryHistoryRequest {
    subjectType: "player" | "staff";
    subjectId: string;
    leagueId: string;
    season: string;
    includeExternal?: boolean;
    settings?: DisciplinarySettings | null;
}

export interface DisciplinaryHistoryResponse {
    sanctions: Sanction[];
    timeline?: any[];
    summary?: any;
    teamId?: string;
}

export async function fetchDisciplinaryHistory(
    supabase: SupabaseClient,
    request: DisciplinaryHistoryRequest
): Promise<DisciplinaryHistoryResponse> {
    const { data, error } = await supabase.functions.invoke("get-disciplinary-history", {
        body: request,
    });

    if (error) {
        throw new Error(`fetchDisciplinaryHistory: ${error.message}`);
    }

    const sanctions = Array.isArray(data?.sanctions)
        ? (data.sanctions as any[]).map(sanctionDbToCamel)
        : [];

    return {
        sanctions,
        timeline: data?.timeline || [],
        summary: data?.summary || null,
        teamId: data?.teamId
    };
}
